import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NdaAgreement } from "@/components/NdaAgreement";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, FileSignature } from "lucide-react";

// Blocks the whole app for authenticated users who have not signed the Beta Participant NDA.
export default function NdaGate() {
  const { user, refreshUser, logout } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  if (!user || user.nda_signed) return null;

  const sign = async () => {
    if (!accepted) { toast.error("Please read the NDA and tick the agreement box first."); return; }
    if (!signature.trim()) { toast.error("Type your full name as your signature."); return; }
    setSaving(true);
    try {
      await api.post("/auth/accept-nda", { nda_accepted: true, nda_signature: signature.trim() });
      toast.success("NDA signed — welcome to the beta!");
      await refreshUser();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not record your signature");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-[#0F2547]/95 backdrop-blur-sm overflow-y-auto" data-testid="nda-gate">
      <div className="min-h-full flex items-center justify-center p-4 py-10">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-1.5">
            <span className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><FileSignature className="h-5 w-5 text-primary" /></span>
            <div>
              <h1 className="font-display text-xl font-extrabold">One thing before you start</h1>
              <p className="text-sm text-muted-foreground">As a beta participant, you need to review and sign the Beta Participant NDA to use Project Management Orchestrator.</p>
            </div>
          </div>
          <div className="mt-4">
            <NdaAgreement accepted={accepted} onAcceptedChange={setAccepted} />
          </div>
          <div className="mt-4">
            <Label htmlFor="nda-gate-signature">Signature (type your full name)</Label>
            <Input id="nda-gate-signature" className="mt-1.5 font-display italic" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Your full name" data-testid="nda-gate-signature-input" />
          </div>
          <Button className="w-full mt-5 h-11 gap-2" onClick={sign} disabled={saving || !accepted || !signature.trim()} data-testid="nda-gate-sign-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Sign &amp; enter the beta
          </Button>
          <button onClick={logout} className="w-full text-center text-xs text-muted-foreground hover:text-foreground mt-3" data-testid="nda-gate-logout">
            I don't agree — sign me out
          </button>
        </div>
      </div>
    </div>
  );
}
